/**
 * Shared display metadata for post-backed entity kinds.
 * Mirrors the existing create-side registry in `postKindConfig.tsx`.
 */
import {
  Blocks,
  Group,
  HandFist,
  MessageSquarePlus,
  OctagonAlert,
  Speech,
  type LucideIcon,
} from "lucide-react";
import type {
  PostCommentMeta,
  PostContextDefaults,
  PostDisplayContext,
  PostDisplayKind,
} from "components/Posts/display/postDisplayTypes";

export interface PostDisplayDefinition {
  kind: PostDisplayKind;
  label: string;
  Icon: LucideIcon;
  chipClassName: string;
  iconClassName: string;
  comment: PostCommentMeta;
}

const POST_DISPLAY_DEFINITIONS: Record<PostDisplayKind, PostDisplayDefinition> = {
  impact: {
    kind: "impact",
    label: "Impact",
    Icon: OctagonAlert,
    chipClassName: "bg-yellow-500 text-white",
    iconClassName: "size-4",
    comment: {
      routeBase: "/impact",
      commentableType: "impact",
      tabLabel: "Discussion",
    },
  },
  teabate: {
    kind: "teabate",
    label: "Teabate",
    Icon: Speech,
    chipClassName: "bg-eoe-entity-teabate text-white",
    iconClassName: "size-4",
    comment: {
      routeBase: "/teabate",
      commentableType: "teabate",
      tabLabel: "Discussion",
    },
  },
  solution: {
    kind: "solution",
    label: "Solution",
    Icon: Blocks,
    chipClassName: "bg-eoe-entity-solution text-white",
    iconClassName: "size-3",
    comment: {
      routeBase: "/solution",
      commentableType: "solution",
      tabLabel: "Comments",
    },
  },
  action: {
    kind: "action",
    label: "Action",
    Icon: HandFist,
    chipClassName: "bg-eoe-entity-action text-white",
    iconClassName: "size-3",
    comment: {
      routeBase: "/action",
      commentableType: "action",
      tabLabel: "Comments",
    },
  },
  collection: {
    kind: "collection",
    label: "Collection",
    Icon: Group,
    chipClassName: "bg-eoe-entity-collection text-white",
    iconClassName: "size-3",
    comment: {
      routeBase: "/collection",
      commentableType: "collection",
      tabLabel: "Comments",
    },
  },
  feedback: {
    kind: "feedback",
    label: "Feedback",
    Icon: MessageSquarePlus,
    chipClassName: "bg-indigo-600 text-white",
    iconClassName: "size-3",
    comment: {
      routeBase: "/feedback",
      commentableType: "feedback",
      tabLabel: "Comments",
    },
  },
};

const POST_CONTEXT_DEFAULTS: Record<PostDisplayContext, PostContextDefaults> = {
  feed: {
    showMeta: true,
    showActions: true,
    truncateDescription: true,
    isEntitySection: false,
    isEntityProfilePage: false,
  },
  section: {
    showMeta: true,
    showActions: true,
    truncateDescription: true,
    isEntitySection: true,
    isEntityProfilePage: false,
  },
  profile: {
    showMeta: true,
    showActions: true,
    truncateDescription: true,
    isEntitySection: false,
    isEntityProfilePage: true,
  },
  crosspost: {
    showMeta: true,
    showActions: true,
    truncateDescription: true,
    isEntitySection: false,
    isEntityProfilePage: false,
  },
  saved: {
    showMeta: true,
    showActions: true,
    truncateDescription: true,
    isEntitySection: false,
    isEntityProfilePage: false,
  },
  compact: {
    showMeta: true,
    showActions: true,
    truncateDescription: true,
    isEntitySection: false,
    isEntityProfilePage: false,
  },
  carousel: {
    showMeta: true,
    showActions: true,
    truncateDescription: true,
    isEntitySection: true,
    isEntityProfilePage: false,
  },
};

export function getPostDisplayDefinition(kind: PostDisplayKind): PostDisplayDefinition {
  return POST_DISPLAY_DEFINITIONS[kind];
}

export function getPostContextDefaults(context: PostDisplayContext): PostContextDefaults {
  return POST_CONTEXT_DEFAULTS[context];
}

export function getPostCommentMeta(kind: PostDisplayKind): PostCommentMeta {
  return POST_DISPLAY_DEFINITIONS[kind].comment;
}
